import { Grid } from "@mui/material";
import { InfoBox } from "components/InfoBox";
import { AutosaveFormTextField } from "components/Forms/AutosaveFormTextField";
import { formatNumberWithAbbreviation } from "util/formatNumber";
import { prettyPrintWasteClassification } from "../../prettyPrintWasteClassification";
import { useWasteStream, useUpdateWasteStream } from "./api";
import { useCallback } from "react";
import { ManifestType } from "./WasteStreamDetailsModal";

interface WasteStreamInfoSectionProps {
  wasteStreamId: string;
  manifestType: ManifestType;
  facilityState?: string;
}
const getGridSize = (relativeSize: "sm" | "md" | "lg") => {
  switch (relativeSize) {
    case "sm":
      return { xs: 6, sm: 4, xl: 12 / 5 };
    case "md":
      return { xs: 6, sm: 4, xl: 3 };
    case "lg":
      return { xs: 12, md: 12, xl: 6 };
  }
};

export const WasteStreamInfoSection = ({
  manifestType,
  wasteStreamId,
  facilityState,
}: WasteStreamInfoSectionProps) => {
  const { data: wasteStreamData } = useWasteStream(wasteStreamId);
  const [updateWasteStream] = useUpdateWasteStream();
  const wasteStream = wasteStreamData?.wasteStream;

  const handleSaveComment = useCallback(
    async (value: string) => {
      await updateWasteStream({
        variables: {
          input: { id: wasteStreamId, comment: value },
        },
      });
    },
    [wasteStreamId, updateWasteStream]
  );

  return (
    <>
      {/* High-level Waste Stream Info */}
      <Grid container spacing={2} sx={{ alignItems: "flex-start" }}>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={wasteStream?.federalWasteCodes?.join(", ") || ""}
            label="Federal Waste Code"
          />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={wasteStream?.stateWasteCodes?.join(", ") || ""}
            label={`${facilityState} Waste Code`}
          />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={prettyPrintWasteClassification(wasteStream?.classification)}
            label="Classification"
          />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={wasteStream?.managementMethodCode || ""}
            label="Management Method"
          />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={
              manifestType === "vendor"
                ? wasteStream?.vendorFormCode || ""
                : wasteStream?.eManifestFormCode || ""
            }
            label="Form Code"
          />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={
              manifestType === "vendor"
                ? wasteStream?.vendorSourceCode || ""
                : wasteStream?.eManifestSourceCode || ""
            }
            label="Source Code"
          />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={wasteStream?.wasteMinimizationCode || ""}
            label="Waste Minimization Code"
          />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={
              manifestType === "vendor"
                ? wasteStream?.vendorDotDescription || ""
                : wasteStream?.eManifestDotDescription || ""
            }
            label="DOT Description"
          />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={
              manifestType === "vendor"
                ? wasteStream?.vendorAmount
                  ? `${formatNumberWithAbbreviation(
                      wasteStream?.vendorAmount,
                      "lbs"
                    )}`
                  : ""
                : wasteStream?.eManifestAmount
                ? `${formatNumberWithAbbreviation(
                    wasteStream?.eManifestAmount,
                    "lbs"
                  )}`
                : ""
            }
            label={
              manifestType === "vendor"
                ? "Amount (vendor)"
                : "Amount (e‑Manifest)"
            }
          />
        </Grid>
        <Grid size={getGridSize("sm")}>
          <InfoBox
            text={
              manifestType === "vendor"
                ? `${wasteStream?.vendorManifestCount || 0}`
                : `${wasteStream?.eManifestCount || 0}`
            }
            label="Manifests Count"
          />
        </Grid>
        <Grid size={getGridSize("md")}>
          <InfoBox
            text={
              manifestType === "vendor"
                ? `${wasteStream?.vendorManifestLineItemCount || 0}`
                : `${wasteStream?.eManifestLineItemCount || 0}`
            }
            label="Manifests Line Item Count"
          />
        </Grid>
        <Grid size={getGridSize("md")}>
          <InfoBox
            text={
              wasteStream?.lastShipmentDateAllTime
                ? new Date(
                    wasteStream?.lastShipmentDateAllTime
                  ).toLocaleDateString()
                : ""
            }
            label="Last Shipment Date (All Time)"
          />
        </Grid>
        <Grid size={getGridSize("lg")}>
          <AutosaveFormTextField
            onSave={handleSaveComment}
            label="Comment"
            defaultValue={wasteStream?.comment ?? ""}
            multiline={true}
          />
        </Grid>
      </Grid>
    </>
  );
};
